import { useState, useEffect, useCallback } from 'react';
import { userCloudApiFetch as apiFetch } from './userCloudApi';
import { isAboveCD, getQualityTier } from '../hooks/useAudioQuality';

function normalizeId(id) {
  if (!id) return id;
  return String(id).replace(/^qobuz:(album|track|artist):/, '');
}

export {
  loadPlaylists,
  invalidateAndReloadPlaylists,
  createPlaylist,
  addTrackToPlaylist,
  usePlaylists,
} from './useUserPlaylists.jsx';
export { notifyPlaylistsUpdated } from './playlistLibraryNotify';

let _favorites = new Map();
let _favListeners = new Set();
let _favLoaded = false;

function notifyFavListeners() {
  _favListeners.forEach((fn) => fn(new Map(_favorites)));
}

function _albumDedupKey(f) {
  const nAlbumId = normalizeId(f.albumId);
  const nTrackId = normalizeId(f.trackId);
  if (nAlbumId) return `aid:${nAlbumId}`;
  if (nTrackId && nAlbumId === nTrackId) return `aid:${nTrackId}`;
  const title = (f.albumTitle || f.trackTitle || '').toLowerCase().trim();
  const artist = (f.artistName || '').toLowerCase().trim();
  if (title && artist) return `ta:${title}|${artist}`;
  return `id:${nTrackId}`;
}

export async function loadFavorites() {
  // Paginate to get ALL favorites (may exceed single-page limit)
  let allFavs = [];
  let offset = 0;
  const limit = 1000;
  let hasMore = true;

  while (hasMore) {
    const data = await apiFetch(`/api/user/favorites?limit=${limit}&offset=${offset}`);
    if (!data?.favorites) break;
    allFavs = allFavs.concat(data.favorites);
    hasMore = !!data.hasMore && data.favorites.length === limit;
    offset += data.favorites.length;
  }

  if (allFavs.length === 0) return;
  _favorites.clear();

  const data = { favorites: allFavs };

  const albumSeen = new Map();
  const duplicateTrackIds = [];

  for (const f of data.favorites) {
    const dedupKey = _albumDedupKey(f);
    if (albumSeen.has(dedupKey)) {
      duplicateTrackIds.push({ trackId: f.trackId, source: f.source });
      continue;
    }
    albumSeen.set(dedupKey, f.trackId);

    const nAlbumId = normalizeId(f.albumId);
    const nTrackId = normalizeId(f.trackId || f.item_id || '');
    const itemType = nAlbumId && nTrackId === nAlbumId ? 'album' : 'track';
    const itemId = nTrackId;
    const key = `${itemType}:${itemId}`;
    _favorites.set(key, {
      item_type: itemType,
      item_id: itemId,
      source: f.source || 'maestro',
      favoritedAt: f.favoritedAt || null,
      metadata: {
        title: f.trackTitle || '',
        artistName: f.artistName || '',
        albumId: nAlbumId || null,
        albumTitle: f.albumTitle || null,
        coverUrl: f.albumArt || null,
        duration: f.duration || null,
        hires: isAboveCD(f.maxBitDepth, f.maxSampleRate),
        qualityTier: getQualityTier(f.maxBitDepth, f.maxSampleRate),
        maxBitDepth: f.maxBitDepth || null,
        maxSampleRate: f.maxSampleRate || null,
        favoritedAt: f.favoritedAt || null,
      },
    });
  }
  _favLoaded = true;
  notifyFavListeners();

  if (duplicateTrackIds.length > 0) {
    console.log('[favorites] Cleaning up', duplicateTrackIds.length, 'duplicate album entries');
    for (const dup of duplicateTrackIds) {
      const qs = dup.source ? `?source=${encodeURIComponent(dup.source)}` : '';
      apiFetch(`/api/user/favorites/${encodeURIComponent(String(dup.trackId))}${qs}`, 'DELETE').catch(() => {});
    }
  }
}

export async function toggleFavorite(itemType, itemId, source, metadata) {
  const nId = normalizeId(itemId);
  const key = `${itemType}:${nId}`;
  if (_favorites.has(key)) {
    _favorites.delete(key);
    notifyFavListeners();
    await apiFetch(`/api/user/favorites/${encodeURIComponent(String(nId))}`, 'DELETE');
  } else {
    const meta = metadata || {};
    const nAlbumId = normalizeId(meta.albumId);
    const fav = {
      item_type: itemType,
      item_id: nId,
      source: source || 'maestro',
      favoritedAt: new Date().toISOString(),
      metadata: { ...meta, albumId: nAlbumId || meta.albumId || null, favoritedAt: new Date().toISOString() },
    };
    const newMap = new Map();
    newMap.set(key, fav);
    _favorites.forEach((v, k) => { if (k !== key) newMap.set(k, v); });
    _favorites = newMap;
    notifyFavListeners();
    await apiFetch('/api/user/favorites', 'POST', {
      trackId: nId,
      trackTitle: meta.title || meta.trackTitle || '',
      artistName: meta.artistName || meta.artist || '',
      albumId: itemType === 'album' ? nId : (nAlbumId || null),
      albumTitle: meta.albumTitle || null,
      albumArt: meta.coverUrl || meta.albumArt || null,
      source: source || 'maestro',
      duration: meta.duration || null,
      hires: isAboveCD(meta.maxBitDepth, meta.maxSampleRate),
      qualityTier: getQualityTier(meta.maxBitDepth, meta.maxSampleRate),
      maxBitDepth: meta.maxBitDepth || null,
      maxSampleRate: meta.maxSampleRate || null,
    });
  }
}

export function isFavorite(itemType, itemId) {
  return _favorites.has(`${itemType}:${normalizeId(itemId)}`);
}

export function useFavorites() {
  const [favs, setFavs] = useState(() => new Map(_favorites));
  useEffect(() => {
    const handler = (m) => setFavs(m);
    _favListeners.add(handler);
    if (!_favLoaded) loadFavorites();
    return () => _favListeners.delete(handler);
  }, []);
  const toggle = useCallback((itemType, itemId, source, metadata) => toggleFavorite(itemType, itemId, source, metadata), []);
  const check = useCallback((itemType, itemId) => favs.has(`${itemType}:${normalizeId(itemId)}`), [favs]);
  const list = useCallback((itemType) => {
    const out = [];
    favs.forEach((v) => { if (!itemType || v.item_type === itemType) out.push(v); });
    return out;
  }, [favs]);
  return { favorites: favs, toggleFavorite: toggle, isFavorite: check, listFavorites: list };
}

